import { CANVAS_SIZE } from "@/features/draw/constants/canvas";
import type { HistoryEntry, Layer, Tool } from "@/features/draw/types";
import { clearHistory, saveHistory } from "@/features/draw/utils/indexedDB";
import i18n from "@/locales";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

// 创建空像素数组
const createEmptyPixels = (): Uint32Array => {
  return new Uint32Array(CANVAS_SIZE * CANVAS_SIZE);
};

const clonePixels = (pixels: Uint32Array): Uint32Array => {
  return new Uint32Array(pixels);
};

export const useAvatarStore = defineStore("avatar", () => {
  const MAX_HISTORY_LENGTH = 10;
  // 基础状态
  const selectedTool = ref<Tool>("brush");
  const currentColor = ref("#000000");
  const brushSize = ref(2);
  const selectedLayerId = ref(1);
  const highlightedColors = ref<string[]>([]);
  const viewMode = ref<"pixel" | "raw">("pixel");
  const latestRawImageUrl = ref("");

  const positivePrompt = ref(i18n.global.t("Message.positivePrompt"));
  const negativePrompt = ref("");

  //教学
  const guidUserDrawCountRemain = ref(3);
  const finishGuidUserDraw = ref(false);
  const guidUserString = ref(i18n.global.t("Message.guidUserString"));

  // 进度状态
  const isGenerating = ref(false);
  const progress = ref(0);
  const isWaitingForImage = ref(false);
  const isFullScreenPreview = ref(false);
  const isHistoryVisible = ref(false);

  // 控制器状态
  const isControlsVisible = ref(false);

  const initLayers = () => {
    return [
      {
        id: 1,
        name: "Layer 1",
        visible: true,
        pixels: createEmptyPixels(),
        requiresPrevious: null,
        version: 0,
      },
    ];
  };

  const initHistory = () => {
    return [
      {
        layers: initLayers(),
        timestamp: Date.now(),
        prompt: "",
        strength: 0.5,
        lineWeight: 0.5,
      },
    ];
  };

  // 图层状态
  const layers = ref<Layer[]>(initLayers());

  // 历史记录状态
  const history = ref<HistoryEntry[]>(initHistory());

  const currentHistoryIndex = ref(0);

  // 选区状态
  const selectedArea = ref<Uint8Array>(
    new Uint8Array(CANVAS_SIZE * CANVAS_SIZE)
  );
  const isSelectingArea = ref(false);

  const selectedLayer = computed(() => {
    return layers.value.find((layer) => layer.id === selectedLayerId.value);
  });

  // 计算属性
  // const mergedLayerPixels = computed(() => {
  //   const finalPixels = createEmptyPixels();

  //   // 从底到顶渲染图层
  //   layers.value
  //     .slice()
  //     .reverse()
  //     .forEach((layer) => {
  //       if (layer.visible) {
  //         const pixels = layer.pixels;
  //         pixels.forEach((color, index) => {
  //           if (color !== 0) {
  //             finalPixels[index] = color;
  //           }
  //         });
  //       }
  //     });
  //   return finalPixels;
  // });

  const mergedLayerPixels = computed<Uint32Array>(() => {
    return selectedLayer.value?.pixels ?? createEmptyPixels();
  });

  // 历史记录管理
  const addHistoryEntry = (data?: {
    prompt?: string;
    strength?: number;
    lineWeight?: number;
    imageUrl?: string;
    tool?: string;
    extractedPrompt?: string;
  }) => {
    // 如果不在最新状态，移除未来历史
    if (currentHistoryIndex.value < history.value.length - 1) {
      history.value = history.value.slice(0, currentHistoryIndex.value + 1);
    }

    const currentState = history.value[currentHistoryIndex.value];

    // 添加新记录（深拷贝当前状态）
    history.value.push({
      layers: layers.value.map((layer) => ({
        ...layer,
        pixels: clonePixels(layer.pixels),
      })),
      timestamp: Date.now(),
      prompt: data?.prompt ?? currentState?.prompt ?? "",
      strength: data?.strength ?? currentState?.strength ?? 0.5,
      lineWeight: data?.lineWeight ?? currentState?.lineWeight ?? 0.5,
      imageUrl: data?.imageUrl,
      tool: data?.tool,
      extractedPrompt: data?.extractedPrompt,
    });
    currentHistoryIndex.value = history.value.length - 1;

    // 限制历史记录最多保存10条，超过则删除最旧的记录
    if (history.value.length > MAX_HISTORY_LENGTH) {
      // 删除最旧的记录，保留最新的10条
      history.value = history.value.slice(
        history.value.length - MAX_HISTORY_LENGTH
      );
      // 更新历史索引指向最新记录
      currentHistoryIndex.value = history.value.length - 1;
    }

    // 自动保存历史记录
    saveHistory(history.value).catch((error) => {
      console.error("Failed to save history:", error);
    });
  };

  // 图层操作
  const updateLayerPixels = (
    layerId: number,
    newPixels: Uint32Array,
    forceAddToHistory: boolean = true
  ) => {
    const targetLayer = layers.value.find((l) => l.id === layerId);
    if (!targetLayer) return;
    targetLayer.pixels = newPixels;
    targetLayer.version++;
    if (forceAddToHistory) {
      // Add history entry with the current tool information
      addHistoryEntry({
        tool: selectedTool.value,
      });
    }
  };

  const resetHistory = () => {
    return [
      {
        layers: getDefaultLayers(),
        timestamp: Date.now(),
        prompt: "",
        strength: 0.5,
        lineWeight: 0.5,
      },
    ];
  };

  const clearAllLayers = () => {
    layers.value.forEach((layer) => {
      layer.pixels = createEmptyPixels();
      layer.version = 0;
    });
    clearHistory();
  };

  const clearLayer = (layerId: number) => {
    updateLayerPixels(layerId, createEmptyPixels());
  };

  const updateLayerVisibility = (layerId: number, visible: boolean) => {
    const layer = layers.value.find((l) => l.id === layerId);
    if (layer) {
      layer.visible = visible;
    }
  };

  const createLayer = () => {
    const newLayerId = Math.max(...layers.value.map((l) => l.id)) + 1;
    const newLayer: Layer = {
      id: newLayerId,
      name: `Layer ${newLayerId}`,
      visible: true,
      pixels: createEmptyPixels(),
      requiresPrevious: null,
    };
    layers.value.unshift(newLayer);
    selectedLayerId.value = newLayerId;
    addHistoryEntry({
      tool: "create-layer",
    });
  };

  const deleteLayer = (layerId: number) => {
    // 不删除最后一个图层
    if (layers.value.length <= 1) {
      return;
    }

    // 移除图层
    layers.value = layers.value.filter((layer) => layer.id !== layerId);

    // 如果删除了当前选中的图层，选择另一个
    if (selectedLayerId.value === layerId) {
      selectedLayerId.value = layers.value[0].id;
    }

    addHistoryEntry({
      tool: "delete-layer",
    });
  };

  // 撤销/重做操作
  const undo = () => {
    if (currentHistoryIndex.value > 0) {
      currentHistoryIndex.value--;
      const historicState = history.value[currentHistoryIndex.value];
      layers.value = historicState.layers.map((layer) => ({
        ...layer,
        pixels: clonePixels(layer.pixels),
      }));
      return historicState.timestamp;
    }
    return null;
  };

  const redo = () => {
    if (currentHistoryIndex.value < history.value.length - 1) {
      currentHistoryIndex.value++;
      const historicState = history.value[currentHistoryIndex.value];
      layers.value = historicState.layers.map((layer) => ({
        ...layer,
        pixels: clonePixels(layer.pixels),
      }));
      return historicState.timestamp;
    }
    return null;
  };

  // 选区操作
  const startAreaSelection = (layerId: number) => {
    selectedLayerId.value = layerId;
    isSelectingArea.value = true;
    selectedArea.value = new Uint8Array(CANVAS_SIZE * CANVAS_SIZE);
  };

  const handleSelectionChange = (newSelection: Uint8Array) => {
    selectedArea.value = newSelection;
  };

  // 视图模式操作
  const setViewMode = (mode: "pixel" | "raw") => {
    viewMode.value = mode;
  };

  return {
    // 状态
    selectedTool,
    currentColor,
    brushSize,
    selectedLayerId,
    highlightedColors,
    viewMode,
    latestRawImageUrl,
    positivePrompt,
    negativePrompt,
    isGenerating,
    progress,
    isWaitingForImage,
    isFullScreenPreview,
    isHistoryVisible,
    layers,
    history,
    currentHistoryIndex,
    selectedArea,
    isSelectingArea,
    mergedLayerPixels,

    guidUserDrawCountRemain,
    finishGuidUserDraw,
    isControlsVisible,
    guidUserString,
    selectedLayer,
    MAX_HISTORY_LENGTH,

    // 方法
    addHistoryEntry,
    updateLayerPixels,
    clearAllLayers,
    clearLayer,
    updateLayerVisibility,
    createLayer,
    deleteLayer,
    undo,
    redo,
    startAreaSelection,
    handleSelectionChange,
    setViewMode,
    initHistory,
  };
});
